/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useCallback, useEffect, useRef, type FC } from "react";
import { useTranslation } from "react-i18next";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@ui/Form";
import { Label } from "@ui/Label";
import { RadioGroup, RadioGroupItem } from "@ui/RadioGroup";
import { Separator } from "@ui/Separator";
import { TABLE_HOURS_COUNT } from "@/constants/validation";
import { useMultiStepForm } from "@/hooks";
import { PROFILE_METHODS, type EnergyProfileMethod } from "@/utils/step1Schema";
import type { StepProps } from "../StepProps";
import { FileSubForm } from "./FileSubForm";
import { ProfileSubForm } from "./ProfileSubForm";
import { TableSubForm } from "./TableSubForm";

const PROFILE_SUBFORMS: Record<EnergyProfileMethod, FC> = {
  PREDEFINED_PROFILE: ProfileSubForm,
  TABLE_INPUT: TableSubForm,
  FILE_INPUT: FileSubForm,
};

export const Step1: FC<StepProps> = ({ additionalItems }) => {
  const { form } = useMultiStepForm();
  const { t } = useTranslation();

  const method = form.watch("step1.energyProfileMethod") as EnergyProfileMethod | undefined;
  const SubForm = method ? PROFILE_SUBFORMS[method] : null;

  const prevMethodRef = useRef<EnergyProfileMethod | undefined>(undefined);

  const clearSubformValues = useCallback(
    (m: EnergyProfileMethod | undefined) => {
      if (!m) return;

      form.clearErrors("step1");

      switch (m) {
        case "PREDEFINED_PROFILE":
          form.setValue("step1.predefinedProfile", undefined, { shouldDirty: false });
          form.setValue("step1.consumptionType", undefined, { shouldDirty: false });
          form.setValue("step1.consumption", "", { shouldDirty: false });
          break;

        case "TABLE_INPUT":
          form.setValue("step1.tableData", Array(TABLE_HOURS_COUNT).fill(""), {
            shouldDirty: false,
            shouldTouch: false,
          });
          form.clearErrors("step1.tableData");
          break;

        case "FILE_INPUT":
          form.setValue("step1.excelProfile", undefined, { shouldDirty: false });
          form.clearErrors("step1.excelProfile");
          break;
      }
    },
    [form],
  );

  useEffect(() => {
    if (!method) {
      prevMethodRef.current = method;
      return;
    }

    const prev = prevMethodRef.current;

    if (prev && prev !== method) {
      clearSubformValues(prev);
    }

    prevMethodRef.current = method;
  }, [method, clearSubformValues]);

  return (
    <>
      <FormField
        control={form.control}
        name="step1.energyProfileMethod"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("step1.q1")}</FormLabel>
            <FormControl>
              <RadioGroup
                className="gap-3 px-2 md:px-4"
                value={field.value ?? ""}
                onValueChange={field.onChange}
              >
                {PROFILE_METHODS.map((option, index) => (
                  <div className="flex items-center space-x-3" key={index}>
                    <RadioGroupItem value={option} id={option} />
                    <Label data-test={`step1-method-${option}`} htmlFor={option}>
                      {t(`step1.q1_${option}`)}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {SubForm && (
        <>
          <Separator />
          <SubForm />
        </>
      )}

      {additionalItems}
    </>
  );
};
